/**
 * Supply Orders Dual-Write Test
 *
 * Tests that supply order operations in Firestore are properly synced to Supabase.
 *
 * Test variations:
 * SO1: Create supply order
 * SO2: Update supply order
 * SO3: Delete supply order
 */

import {
  saveNewSupplyOrder,
  updateSupplyOrder,
  deleteSupplyOrder,
} from '../global/Firebase/firebase';
import { api } from '../global/API/apiClient';
import { getDefaultStore } from 'jotai';
import { organizationAtom, storesDictAtom, suppliersDictAtom, productsDictAtom, dbAtom, organizationIdAtom } from '../global/AtomsProvider';

// Test variation definitions
export const SUPPLY_ORDERS_TEST_VARIATIONS = [
  { id: 'SO1', category: 'create', name: 'Create supply order' },
  { id: 'SO2', category: 'update', name: 'Update supply order status' },
  { id: 'SO3', category: 'delete', name: 'Delete supply order' },
];

// Get test categories for UI grouping
export function getSupplyOrdersTestCategories() {
  return [
    { id: 'create', name: 'Create Supply Order', variations: ['SO1'] },
    { id: 'update', name: 'Update Supply Order', variations: ['SO2'] },
    { id: 'delete', name: 'Delete Supply Order', variations: ['SO3'] },
  ];
}

// Helper to normalize values for comparison
function normalizeValue(val) {
  if (val === undefined || val === null) return null;
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    const num = parseFloat(val);
    if (!isNaN(num)) return num;
    return val.trim();
  }
  return val;
}

// Helper to compare values with tolerance for floating point
function valuesMatch(a, b, tolerance = 0.01) {
  const normA = normalizeValue(a);
  const normB = normalizeValue(b);

  if (normA === normB) return true;
  if (normA === null || normB === null) return normA === normB;

  // Numeric comparison with tolerance
  if (typeof normA === 'number' && typeof normB === 'number') {
    return Math.abs(normA - normB) < tolerance;
  }

  return normA === normB;
}

/**
 * Get test data based on atoms
 */
export function getSupplyOrdersTestData() {
  const jotaiStore = getDefaultStore();
  const organization = jotaiStore.get(organizationAtom);
  const storesDict = jotaiStore.get(storesDictAtom);
  const suppliersDict = jotaiStore.get(suppliersDictAtom);
  const productsDict = jotaiStore.get(productsDictAtom);
  const db = jotaiStore.get(dbAtom);
  const organizationId = jotaiStore.get(organizationIdAtom);

  if (!organization || !db) {
    return null;
  }

  // Get first store
  const storeEntries = Object.entries(storesDict || {});
  if (storeEntries.length < 1) {
    return null;
  }

  const store = { id: storeEntries[0][0], ...storeEntries[0][1] };

  // Get first supplier
  const supplierEntries = Object.entries(suppliersDict || {});
  const supplier = supplierEntries.length > 0
    ? { id: supplierEntries[0][0], ...supplierEntries[0][1] }
    : { id: 'test-supplier', name: 'Test Supplier' };

  // Get first product for order items
  const productEntries = Object.entries(productsDict || {});
  const product = productEntries.length > 0
    ? { id: productEntries[0][0], ...productEntries[0][1] }
    : { id: 'test-product', name: 'Test Product', price: 100 };

  return {
    organization,
    store,
    supplier,
    product,
    db,
    organizationId,
  };
}

/**
 * Compare supply order via backend API
 */
async function compareSupplyOrderViaApi(orderId, orderData, log) {
  try {
    const response = await api.get(`/supply-orders/compare/${orderId}`);
    const apiResult = response.data;

    if (!apiResult.exists) {
      return {
        orderId,
        foundInSupabase: false,
        matches: false,
        differences: [{
          field: 'supply_order',
          supabaseValue: null,
          firestoreValue: 'exists',
          severity: 'critical',
        }],
        warnings: [],
      };
    }

    const supabaseData = apiResult.supabaseData;
    const differences = [];

    // Compare key fields
    if (orderData.status) {
      const sbStatus = normalizeValue(supabaseData?.status);
      const fsStatus = normalizeValue(orderData.status);
      if (!valuesMatch(sbStatus, fsStatus)) {
        differences.push({
          field: 'status',
          supabaseValue: sbStatus,
          firestoreValue: fsStatus,
          severity: 'critical',
        });
      }
    }

    // Compare items count (lenient comparison for JSONB)
    const sbItemsCount = Array.isArray(supabaseData?.items) ? supabaseData.items.length : 0;
    const fsItemsCount = Array.isArray(orderData.items) ? orderData.items.length : 0;
    if (sbItemsCount !== fsItemsCount) {
      differences.push({
        field: 'items_count',
        supabaseValue: sbItemsCount,
        firestoreValue: fsItemsCount,
        severity: 'warning',
      });
    }

    return {
      orderId,
      foundInSupabase: true,
      matches: differences.filter(d => d.severity === 'critical').length === 0,
      differences,
      warnings: differences.filter(d => d.severity === 'warning').map(d => d.field),
      supabaseData,
    };
  } catch (error) {
    log(`API comparison error: ${error.message}`);
    return {
      orderId,
      foundInSupabase: false,
      matches: false,
      differences: [{
        field: 'api_error',
        supabaseValue: null,
        firestoreValue: error.message,
        severity: 'critical',
      }],
      warnings: [`API error: ${error.message}`],
    };
  }
}

/**
 * Check if supply order was deleted from Supabase
 */
async function checkSupplyOrderDeleted(orderId, maxRetries = 3) {
  for (let i = 0; i < maxRetries; i++) {
    try {
      const response = await api.get(`/supply-orders/compare/${orderId}`);
      if (!response.data.exists) {
        return true;
      }
      // Wait before retry
      await new Promise(resolve => setTimeout(resolve, 2000));
    } catch (error) {
      // 404 means deleted
      if (error.response?.status === 404) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Run a single supply order test
 */
async function runSingleTest(variationId, testData, log) {
  const { db, store, supplier, product, organizationId } = testData;

  let createdRef = null;
  let result = {
    variationId,
    passed: false,
    error: null,
    comparisonResult: null,
  };

  try {
    switch (variationId) {
      case 'SO1': {
        // Create supply order
        const orderData = {
          store: store.id,
          organizationId,
          supplierID: supplier.id,
          status: 'pending',
          cashierUID: 'test-cashier-uid', // Required by Firestore saveNewSupplyOrder
          items: [{
            productId: product.id,
            productName: product.name || 'Test Product',
            quantity: 5,
            price: product.price || 100,
          }],
          expectedDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(), // 7 days from now
        };

        log(`Creating supply order for supplier: ${supplier.name || supplier.id}`);
        createdRef = await saveNewSupplyOrder(db, orderData);

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Compare
        const comparison = await compareSupplyOrderViaApi(createdRef.id, orderData, log);
        result.comparisonResult = comparison;
        result.passed = comparison.foundInSupabase;

        // Cleanup
        log('Cleaning up test supply order...');
        await deleteSupplyOrder(db, createdRef, {});
        await new Promise(resolve => setTimeout(resolve, 2000));

        break;
      }

      case 'SO2': {
        // Update supply order test
        const orderData = {
          store: store.id,
          organizationId,
          supplierID: supplier.id,
          status: 'pending',
          cashierUID: 'test-cashier-uid', // Required by Firestore saveNewSupplyOrder
          items: [{
            productId: product.id,
            productName: product.name || 'Test Product',
            quantity: 3,
            price: product.price || 100,
          }],
        };

        log('Creating supply order to update...');
        createdRef = await saveNewSupplyOrder(db, orderData);

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Verify it exists first
        const createCheck = await compareSupplyOrderViaApi(createdRef.id, orderData, log);
        if (!createCheck.foundInSupabase) {
          result.error = 'Supply order was not created in Supabase';
          break;
        }

        // Now update via Firestore
        const newStatus = 'confirmed';
        log(`Updating supply order status to: ${newStatus}`);
        await updateSupplyOrder(db, createdRef, { status: newStatus });

        log('Waiting 6000ms for update sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Compare updated values
        const updateCheck = await compareSupplyOrderViaApi(createdRef.id, { ...orderData, status: newStatus }, log);
        result.comparisonResult = updateCheck;
        result.passed = updateCheck.foundInSupabase && valuesMatch(updateCheck.supabaseData?.status, newStatus);

        // Cleanup
        log('Cleaning up test supply order...');
        await deleteSupplyOrder(db, createdRef, {});
        await new Promise(resolve => setTimeout(resolve, 2000));

        break;
      }

      case 'SO3': {
        // Delete supply order test
        const orderData = {
          store: store.id,
          organizationId,
          supplierID: supplier.id,
          status: 'pending',
          cashierUID: 'test-cashier-uid', // Required by Firestore saveNewSupplyOrder
          items: [{
            productId: product.id,
            productName: product.name || 'Test Product',
            quantity: 2,
            price: product.price || 100,
          }],
        };

        log('Creating supply order to delete...');
        createdRef = await saveNewSupplyOrder(db, orderData);

        log('Waiting 6000ms for Supabase sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Verify it exists first
        const createCheck = await compareSupplyOrderViaApi(createdRef.id, orderData, log);
        if (!createCheck.foundInSupabase) {
          result.error = 'Supply order was not created in Supabase';
          break;
        }

        // Now delete
        log('Deleting supply order...');
        await deleteSupplyOrder(db, createdRef, {});

        log('Waiting 6000ms for delete sync...');
        await new Promise(resolve => setTimeout(resolve, 6000));

        // Check if deleted
        const deleted = await checkSupplyOrderDeleted(createdRef.id);
        result.passed = deleted;
        result.comparisonResult = {
          orderId: createdRef.id,
          foundInSupabase: !deleted,
          matches: deleted,
          differences: deleted ? [] : [{
            field: 'deleted',
            supabaseValue: 'still exists',
            firestoreValue: 'deleted',
            severity: 'critical',
          }],
        };

        // No cleanup needed - already deleted
        createdRef = null;

        break;
      }

      default:
        result.error = `Unknown variation: ${variationId}`;
    }
  } catch (error) {
    result.error = error.message;
    log(`Error: ${error.message}`);

    // Cleanup on error
    if (createdRef) {
      try {
        await deleteSupplyOrder(db, createdRef, {});
      } catch (cleanupError) {
        log(`Cleanup failed: ${cleanupError.message}`);
      }
    }
  }

  return result;
}

/**
 * Run all supply orders tests
 */
export async function runFullSupplyOrdersTest(log = console.log, selectedVariations = null) {
  log('Starting supply orders dual-write tests...');

  const testData = getSupplyOrdersTestData();
  if (!testData) {
    log('ERROR: Could not get test data. Need at least 1 store.');
    return { passed: 0, failed: 0, skipped: 0, results: [] };
  }

  log('\n========================================');
  log('SUPPLY ORDERS DUAL-WRITE TEST SUITE');
  log('========================================');
  log(`Organization: ${testData.organization.name || testData.organizationId}`);
  log(`Store: ${testData.store.name || testData.store.id}`);
  log(`Supplier: ${testData.supplier.name || testData.supplier.id}`);
  log('========================================\n');

  const variationsToRun = selectedVariations || SUPPLY_ORDERS_TEST_VARIATIONS.map(v => v.id);
  const results = [];
  let passed = 0;
  let failed = 0;
  let skipped = 0;

  for (const variationId of variationsToRun) {
    const variation = SUPPLY_ORDERS_TEST_VARIATIONS.find(v => v.id === variationId);
    if (!variation) {
      log(`Skipping unknown variation: ${variationId}`);
      skipped++;
      continue;
    }

    log(`\n--- Running test ${variationId}: ${variation.name} ---`);

    const result = await runSingleTest(variationId, testData, log);
    results.push(result);

    if (result.error) {
      log(`ERROR: ${result.error}`);
      failed++;
    } else if (result.passed) {
      log(`PASS: ${variation.name}`);
      passed++;
    } else {
      log(`FAIL: ${variation.name}`);
      if (result.comparisonResult?.differences?.length > 0) {
        log('Differences: ' + JSON.stringify(result.comparisonResult.differences, null, 2));
      }
      failed++;
    }
  }

  log('\n========================================');
  log('TEST SUMMARY');
  log('========================================');
  log(`Passed: ${passed}`);
  log(`Failed: ${failed}`);
  log(`Skipped: ${skipped}`);
  log('========================================');

  if (failed > 0) {
    log('\nFAILED TESTS:');
    results.filter(r => !r.passed && !r.error).forEach(r => {
      const variation = SUPPLY_ORDERS_TEST_VARIATIONS.find(v => v.id === r.variationId);
      log(`  - ${r.variationId}: ${variation?.name}`);
      r.comparisonResult?.differences?.forEach(d => {
        log(`    ${d.field}: Supabase="${d.supabaseValue}" vs Firestore="${d.firestoreValue}"`);
      });
    });
  }

  return { passed, failed, skipped, results };
}

export default {
  SUPPLY_ORDERS_TEST_VARIATIONS,
  getSupplyOrdersTestCategories,
  getSupplyOrdersTestData,
  runFullSupplyOrdersTest,
};
